import { z } from 'zod';
import { CursorSchema, SlugSchema, TimestampSchema } from '../common.js';
import { AgentSlugSchema } from '../common.js';
import { ProductTypeSchema, SessionListItemSchema, SessionOutcomeSchema, SessionSchema, } from '../entities/session.js';
import { KnownSessionEventTypeSchema, SessionEventSchema } from '../entities/sessionEvent.js';
import { KnownTurnVoiceProviderSchema, TranscriptRoleSchema, TranscriptTurnSchema, TurnUsageSchema, } from '../entities/transcriptTurn.js';
// =========================================================
// POST /v1/public/sessions — create a session
// =========================================================
export const SessionCreateRequestSchema = z.object({
    productType: ProductTypeSchema,
    tenantSlug: SlugSchema,
    productSlug: AgentSlugSchema,
    origin: z.string().nullable(),
});
export const SessionTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const SessionCreateResponseSchema = z.object({
    sessionId: z.string().uuid(),
    startedAt: TimestampSchema,
    /** One-time public-session proof. The backend stores only its SHA-256 hash. */
    sessionToken: SessionTokenSchema,
    sessionTokenExpiresAt: TimestampSchema,
});
// =========================================================
// POST /v1/public/sessions/{sessionId}/events — append events (batch)
// =========================================================
/** Max number of events per batch call — bounded to match DDB BatchWrite chunking. */
export const MAX_EVENTS_PER_BATCH = 50;
/** One inbound event — server fills in `eventId` + `sessionId` + `tenantId`. */
export const SessionEventInputSchema = z.object({
    type: KnownSessionEventTypeSchema,
    timestamp: TimestampSchema,
    payload: z.record(z.unknown()).optional(),
});
export const SessionAppendEventsRequestSchema = z.object({
    events: z.array(SessionEventInputSchema).min(1).max(MAX_EVENTS_PER_BATCH),
});
export const SessionAppendEventsResponseSchema = z.object({
    accepted: z.number().int().nonnegative(),
});
// =========================================================
// POST /v1/public/sessions/{sessionId}/turns — append transcript turns
// =========================================================
export const MAX_TURNS_PER_BATCH = 50;
/**
 * Append-payload shape for one transcript turn. Mirrors `TranscriptTurnSchema`
 * minus the server-managed identity fields (turnId/sessionId/tenantId/seq).
 *
 * `costUsd` is intentionally NOT accepted — the server computes it at ingest
 * from the SSM voice-rates snapshot in effect, so historical rows can't drift
 * when rates change later. Phase-1 decision; see backend/src/services/voiceRates.ts.
 */
export const TranscriptTurnInputSchema = z.object({
    role: TranscriptRoleSchema,
    text: z.string(),
    timestamp: TimestampSchema,
    audioMs: z.number().int().nonnegative().optional(),
    latencyMs: z.number().int().nonnegative().optional(),
    modelUsed: z.string().min(1).max(120).optional(),
    voiceUsed: z.string().min(1).max(80).optional(),
    voiceProvider: KnownTurnVoiceProviderSchema.optional(),
    usage: z
        .object({
        inputTokens: z.number().int().nonnegative(),
        inputTokensCached: z.number().int().nonnegative(),
        inputTokensCachedText: z.number().int().nonnegative().optional(),
        inputTokensCachedAudio: z.number().int().nonnegative().optional(),
        inputTokensText: z.number().int().nonnegative(),
        inputTokensAudio: z.number().int().nonnegative(),
        outputTokens: z.number().int().nonnegative(),
        outputTokensText: z.number().int().nonnegative(),
        outputTokensAudio: z.number().int().nonnegative(),
        totalTokens: z.number().int().nonnegative(),
    })
        .optional(),
    interrupted: z.boolean().optional(),
    toolCalls: z
        .array(z.object({
        name: z.string().min(1).max(120),
        arguments: z.record(z.unknown()),
        result: z.unknown().optional(),
        durationMs: z.number().int().nonnegative(),
    }))
        .max(20)
        .optional(),
    errorCode: z.string().min(1).max(80).optional(),
});
export const SessionAppendTurnsRequestSchema = z.object({
    turns: z.array(TranscriptTurnInputSchema).min(1).max(MAX_TURNS_PER_BATCH),
});
export const SessionAppendTurnsResponseSchema = z.object({
    accepted: z.number().int().nonnegative(),
    firstSeq: z.number().int().positive(),
    lastSeq: z.number().int().positive(),
});
// =========================================================
// POST /v1/public/sessions/{sessionId}/text-turn — text chat turn
// =========================================================
export const PublicSessionTextTurnRequestSchema = z.object({
    lang: z.string().regex(/^[a-z]{2}$/),
    message: z.string().min(1).max(4_000),
    /** Stable browser-generated id used to make costly turn processing idempotent. */
    clientTurnId: z.string().uuid().optional(),
});
export const PublicSessionTextTurnAnimationSchema = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('queued'),
        animationId: z.string().uuid(),
        renderDeadlineAt: TimestampSchema,
    })
        .strict(),
    z
        .object({
        kind: z.literal('unavailable'),
        retryable: z.boolean(),
    })
        .strict(),
]);
export const PublicSessionTextTurnResponseSchema = z.object({
    reply: z.string().min(1).max(8_192),
    model: z.string().min(1).max(120),
    usage: TurnUsageSchema,
    latencyMs: z.number().int().nonnegative(),
    knowledge: z.object({
        resultCount: z.number().int().nonnegative(),
        durationMs: z.number().int().nonnegative(),
    }),
    /** Omitted for agents that do not have lip animation enabled. */
    animation: PublicSessionTextTurnAnimationSchema.optional(),
});
// =========================================================
// GET /v1/public/sessions/{sessionId}/animations/{animationId}
// =========================================================
const PublicSessionAnimationIdSchema = z.string().uuid();
const PublicSessionAnimationRetryAfterMsSchema = z.number().int().min(250).max(30_000);
const PublicSessionAnimationErrorCodeSchema = z.string().min(1).max(80);
const PublicSessionAnimationPlaybackUrlSchema = z
    .string()
    .max(4_096)
    .url()
    .refine((value) => {
    const protocol = new URL(value).protocol;
    return protocol === 'https:' || protocol === 'http:';
}, 'Playback URL must use HTTP or HTTPS.');
export const PublicSessionAnimationStatusResponseSchema = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('queued'),
        animationId: PublicSessionAnimationIdSchema,
        renderDeadlineAt: TimestampSchema,
        retryAfterMs: PublicSessionAnimationRetryAfterMsSchema,
    })
        .strict(),
    z
        .object({
        kind: z.literal('rendering'),
        animationId: PublicSessionAnimationIdSchema,
        renderDeadlineAt: TimestampSchema,
        retryAfterMs: PublicSessionAnimationRetryAfterMsSchema,
    })
        .strict(),
    z
        .object({
        kind: z.literal('ready'),
        animationId: PublicSessionAnimationIdSchema,
        playbackUrl: PublicSessionAnimationPlaybackUrlSchema,
        expiresAt: TimestampSchema,
    })
        .strict(),
    z
        .object({
        kind: z.literal('failed'),
        animationId: PublicSessionAnimationIdSchema,
        errorCode: PublicSessionAnimationErrorCodeSchema,
        retryable: z.boolean(),
    })
        .strict(),
]);
// =========================================================
// POST /v1/public/sessions/{sessionId}/end — finalise a session
// =========================================================
export const SessionEndRequestSchema = z.object({
    endedAt: TimestampSchema,
    outcome: SessionOutcomeSchema,
});
export const SessionEndResponseSchema = z.object({
    sessionId: z.string().uuid(),
    endedAt: TimestampSchema,
    durationSec: z.number().nonnegative(),
});
// =========================================================
// GET /v1/tenant/agents/{agentId}/sessions — list for agent
// =========================================================
/** Sessions list default/max page size (docs/api.md). */
export const SESSIONS_LIST_DEFAULT_LIMIT = 50;
export const SESSIONS_LIST_MAX_LIMIT = 200;
export const SessionListResponseSchema = z.object({
    items: z.array(SessionListItemSchema),
    nextCursor: CursorSchema.nullable(),
});
// =========================================================
// GET /v1/tenant/sessions/{sessionId} — session detail
// =========================================================
/** Inline cap per sub-collection in the detail endpoint. */
export const SESSION_DETAIL_SUBCOLLECTION_CAP = 500;
export const SessionDetailResponseSchema = z.object({
    session: SessionSchema,
    events: z.array(SessionEventSchema),
    turns: z.array(TranscriptTurnSchema),
    truncated: z.boolean(),
    /**
     * `true` when `session.aggregate` was computed live on the read path from
     * the loaded turns + events (session has no durable aggregate yet — it
     * hasn't been ended, or the user abandoned the tab without /end firing).
     * `false` when the field reflects the stored aggregate written at /end.
     *
     * Live aggregates are best-effort: if `truncated` is true, older turns
     * weren't loaded and the rollup is partial.
     */
    aggregateLive: z.boolean(),
});
// =========================================================
// GET /v1/tenant/agents/{agentId}/metrics — aggregated metrics
// =========================================================
export const AgentMetricsQuerySchema = z.object({
    from: TimestampSchema.optional(),
    to: TimestampSchema.optional(),
});
export const AgentMetricsResponseSchema = z.object({
    window: z.object({ from: TimestampSchema, to: TimestampSchema }),
    sessionsCount: z.number().int().nonnegative(),
    voiceSessionsCount: z.number().int().nonnegative(),
    totalVoiceDurationSec: z.number().nonnegative(),
    avgVoiceDurationSec: z.number().nonnegative(),
    videoCompletionCount: z.number().int().nonnegative(),
    videoCompletionRate: z.number().nonnegative(),
    ctaClicksCount: z.number().int().nonnegative(),
    ctaConversionRate: z.number().nonnegative(),
    transcriptTurnsCount: z.number().int().nonnegative(),
    userQuestionsCount: z.number().int().nonnegative(),
    outcomes: z.object({
        completed: z.number().int().nonnegative(),
        abandoned: z.number().int().nonnegative(),
        'cta-clicked': z.number().int().nonnegative(),
    }),
    // Aggregate-backed totals — summed from each session's `Session.aggregate`
    // block when present, with live read-path hydration from turns/events when
    // the stored aggregate is missing. Phase 4 dashboard renders these in the
    // metrics tab as stat-grid tiles. All values are non-negative; latency
    // numbers are 0 when no assistant turns reported a `latencyMs`.
    totalTokensInput: z.number().int().nonnegative(),
    totalTokensOutput: z.number().int().nonnegative(),
    totalTokensTotal: z.number().int().nonnegative(),
    totalCostUsd: z.number().nonnegative(),
    totalAudioDurationMs: z.number().int().nonnegative(),
    errorCount: z.number().int().nonnegative(),
    interruptionCount: z.number().int().nonnegative(),
    avgLatencyMs: z.number().int().nonnegative(),
    p95LatencyMs: z.number().int().nonnegative(),
});
//# sourceMappingURL=sessions.js.map